/**
 * WindowSpecPatch — Faz 10 dilim 3
 *
 * AI mutate_window tool'unun urettigi id-anchored patch format.
 * applyPatch: immutable deep-clone, id-anchored traversal ile spec gunceller.
 *
 * Op'lar:
 *   set     — targetId'li node'un dotted path'ini deger ile set et
 *   remove  — targetId'li node'u agactan cikar
 *   add     — parentId altina index pozisyonuna yeni node ekle
 *   replace — targetId'li node'u yeni node ile degistir (id miras alinir)
 *
 * Referans: docs/desktop/window-spec.md#mutate
 */
import { z } from 'zod';
import { windowSpecSchema } from './windowSpec.js';
// ─── assignIds ─────────────────────────────────────────────────────────────────
/**
 * Recursive walker: WindowSpec agacindaki her node'a stabil, deterministik id atar.
 * - Root node: id 'root' olarak atanir (LLM mutate_window varsayimi ile uyumlu).
 * - Cocuk node'lar: '${type}-${index}' formatinda prefix ile, child indexi ile
 *   unique id uretilir (ornek: 'Stack-0', 'Card-0', 'Button-1').
 * - Zaten id'si olan node'lar dokunulmaz (idempotent).
 *
 * Compose finalize fazinda cagirilmalidir; mutate patch'lerinin calismasi
 * icin spec'teki tum node'larin id'ye sahip olmasi sart.
 *
 * @param spec - Guncellenecek WindowSpec (in-place mutate)
 * @param parentId - Parent id prefix (recursive icin); root call'da gecilmez
 * @param index - Bu node'un parent.children icindeki indeksi (recursive)
 * @returns Ayni spec referansi (immutable degil — in-place mutate, compose pipeline icin OK)
 */
export function assignIds(spec, parentId, index) {
    if (!spec.id) {
        if (parentId === undefined) {
            // Root node
            spec.id = 'root';
        }
        else {
            // Cocuk node: tip + index ile stabil id
            spec.id = `${spec.type.toLowerCase()}-${index ?? 0}`;
        }
    }
    if (spec.children) {
        spec.children.forEach((child, i) => assignIds(child, spec.id, i));
    }
    return spec;
}
// ─── Op schemas ──────────────────────────────────────────────────────────────
// Recursive WindowSpec Zod schema (inline kopya, import'a gerek yok)
const windowSpecSchema_lazy = z.lazy(() => z.object({
    type: z.string(),
    id: z.string().optional(),
    props: z.record(z.unknown()).optional(),
    children: z.array(windowSpecSchema_lazy).optional(),
    dataSource: z.unknown().optional(),
}));
const setOpSchema = z.object({
    op: z.literal('set'),
    /** ID'si olan node'un anchor'i */
    targetId: z.string().min(1),
    /**
     * Dot-notated prop path — ornek: 'props.label', 'props.variant', 'dataSource.tool', 'dataSource.args.role'
     * Desteklenen kok segmentler: 'type', 'id', 'props', 'dataSource'
     * Max derinlik: 5 segment. Runtime'da setAtPath validate eder.
     */
    path: z.string().min(1),
    value: z.unknown(),
});
const removeOpSchema = z.object({
    op: z.literal('remove'),
    targetId: z.string().min(1),
});
const addOpSchema = z.object({
    op: z.literal('add'),
    parentId: z.string().min(1),
    /** Eklenecek pozisyon — belirtilmezse append */
    index: z.number().int().nonnegative().optional(),
    node: windowSpecSchema_lazy,
});
const replaceOpSchema = z.object({
    op: z.literal('replace'),
    targetId: z.string().min(1),
    /** Yeni node — targetId miras alinir (node.id override edilir) */
    node: windowSpecSchema_lazy,
});
export const windowSpecPatchOpSchema = z.discriminatedUnion('op', [
    setOpSchema,
    removeOpSchema,
    addOpSchema,
    replaceOpSchema,
]);
export const windowSpecPatchSchema = z.object({
    ops: z.array(windowSpecPatchOpSchema).min(1),
});
// ─── Internal helpers ─────────────────────────────────────────────────────────
/** Derin klon — JSON-serializable nesneler icin yeterli */
function deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
}
function findById(root, id) {
    if (root.id === id) {
        return { node: root, parent: null, indexInParent: -1 };
    }
    const stack = [];
    if (root.children) {
        root.children.forEach((child, i) => stack.push({ node: child, parent: root, idx: i }));
    }
    while (stack.length > 0) {
        const item = stack.pop();
        if (item.node.id === id) {
            return { node: item.node, parent: item.parent, indexInParent: item.idx };
        }
        if (item.node.children) {
            item.node.children.forEach((child, i) => stack.push({ node: child, parent: item.node, idx: i }));
        }
    }
    return null;
}
/**
 * Dot-notated path ile spec node uzerinde set islemi.
 * Desteklenen path'ler:
 *   'type'                     — string
 *   'id'                       — string | undefined
 *   'props'                    — Record<string, unknown>
 *   'props.xxx'                — props icindeki herhangi bir alan
 *   'dataSource'               — DataSource nesnesinin tamami
 *   'dataSource.tool'          — dataSource.tool
 *   'dataSource.args'          — dataSource.args (Record)
 *   'dataSource.args.role'     — dataSource.args icindeki alan (3 segment)
 *   n-segment dotted path      — max depth 5
 *   Yeni path yaratma YASAK    — yalnizca mevcut alanlari gunceller
 *
 * Path dogrulama:
 *   - Her segment: [a-zA-Z_][a-zA-Z0-9_]* (alfanumerik, alt cizgi)
 *   - Max 5 segment (depth asiminda aciklayici hata)
 *   - Desteklenmeyen kok segment = hata
 */
const PATH_SEGMENT_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
const MAX_PATH_DEPTH = 5;
/** Desteklenen kok (1. segment) alanlar */
const SUPPORTED_ROOT_FIELDS = new Set(['type', 'id', 'props', 'dataSource']);
/**
 * path_not_settable hatasi — applyPatch endpoint'te 422 olarak yakalanir.
 * `message` formati: 'path_not_settable: <path>' (parsePathFromError icin stabil).
 */
function throwPathNotSettable(path) {
    throw new Error(`path_not_settable: ${path}`);
}
function setAtPath(node, path, value, propsWhitelist) {
    const segments = path.split('.');
    // Max depth kontrol
    if (segments.length > MAX_PATH_DEPTH) {
        throw new Error(`applyPatch set: path '${path}' cok derin — max ${MAX_PATH_DEPTH} segment destekleniyor`);
    }
    // Her segment format dogrulama
    for (const seg of segments) {
        if (!PATH_SEGMENT_RE.test(seg)) {
            throw new Error(`applyPatch set: gecersiz path segment '${seg}' — alfanumerik ve alt cizgi bekleniyor`);
        }
    }
    const [root, ...rest] = segments;
    // Kok segment destekleniyor mu?
    if (!SUPPORTED_ROOT_FIELDS.has(root)) {
        throw new Error(`applyPatch set: desteklenmeyen kok path '${root}' — gecerli alanlar: ${[...SUPPORTED_ROOT_FIELDS].join(', ')}`);
    }
    // Tek segment: kok alani dogrudan set
    if (rest.length === 0) {
        if (root === 'type') {
            node['type'] = value;
        }
        else if (root === 'id') {
            node['id'] = value;
        }
        else if (root === 'props') {
            node.props = value;
        }
        else if (root === 'dataSource') {
            node['dataSource'] = value;
        }
        return;
    }
    // props.X icin whitelist kontrolu (propsWhitelist verilmisse)
    if (root === 'props' && rest.length > 0 && propsWhitelist !== undefined) {
        const propKey = rest[0];
        if (!propsWhitelist.has(propKey)) {
            throwPathNotSettable(path);
        }
    }
    // Cok segment: kok alani altinda derin set
    // Root nesneye eris
    let target;
    if (root === 'props') {
        if (!node.props)
            node.props = {};
        target = node.props;
    }
    else if (root === 'dataSource') {
        if (!node.dataSource) {
            throw new Error(`applyPatch set: path '${path}' — node'un 'dataSource' alani yok; once 'dataSource' set edin`);
        }
        target = node.dataSource;
    }
    else {
        throw new Error(`applyPatch set: '${root}' alani icin alt-path desteklenmiyor`);
    }
    // Derin walk: son segment haric gez
    for (let i = 0; i < rest.length - 1; i++) {
        const seg = rest[i];
        if (target[seg] === undefined || target[seg] === null) {
            // Ara node yok — args gibi opsiyonel nesne ise olustur
            target[seg] = {};
        }
        if (typeof target[seg] !== 'object' || Array.isArray(target[seg])) {
            throw new Error(`applyPatch set: path '${path}' — '${rest.slice(0, i + 1).join('.')}' bir nesne degil, alt-alana erisilemiyor`);
        }
        target = target[seg];
    }
    // Son segment: deger ata
    target[rest[rest.length - 1]] = value;
}
/** Agactaki tum node id'lerini toplar — hata mesajlari icin debug yardimi */
function collectIds(root) {
    const ids = [];
    const stack = [root];
    while (stack.length > 0) {
        const node = stack.pop();
        if (node.id)
            ids.push(node.id);
        if (node.children) {
            node.children.forEach((c) => stack.push(c));
        }
    }
    return ids;
}
/**
 * Immutable patch uygulama.
 * Hata firlatma kosullari:
 *   - targetId/parentId spec'te bulunamiyor
 *   - set icin gecersiz path
 *   - remove root node
 *   - add parentId'si children desteklemeyen (leaf) bir node
 *   - props.X path'i — X, primitive propsSchema whitelist'inde yoksa (path_not_settable)
 *
 * @param spec - Mutate edilecek WindowSpec (immutable — klonlanir)
 * @param patch - Uygulanacak patch ops
 * @param primitiveSchemas - (opsiyonel) node tipi → izin verilen props whitelist lookup;
 *   verilmezse whitelist aktif olmaz (backwards-compat)
 * @returns Yeni WindowSpec (deep clone; original degismez)
 * @throws Error — id bulunamadi, gecersiz path, root remove, path_not_settable
 */
export function applyPatch(spec, patch, primitiveSchemas) {
    // Deep clone — orjinal dokunulmaz
    let root = deepClone(spec);
    for (const op of patch.ops) {
        switch (op.op) {
            case 'set': {
                if (!op.targetId) {
                    throw new Error(`applyPatch set: targetId bos veya tanimsiz — LLM patch gecersiz. ` +
                        `Mevcut id'ler: [${collectIds(root).join(', ')}]`);
                }
                const found = findById(root, op.targetId);
                if (!found) {
                    throw new Error(`applyPatch set: id '${op.targetId}' bulunamadi. ` +
                        `Mevcut id'ler: [${collectIds(root).join(', ')}]`);
                }
                // primitive propsSchema whitelist lookup — node tipine gore
                const propsWhitelist = primitiveSchemas?.get(found.node.type);
                setAtPath(found.node, op.path, op.value, propsWhitelist);
                break;
            }
            case 'remove': {
                if (!op.targetId) {
                    throw new Error(`applyPatch remove: targetId bos veya tanimsiz — LLM patch gecersiz. ` +
                        `Mevcut id'ler: [${collectIds(root).join(', ')}]`);
                }
                const found = findById(root, op.targetId);
                if (!found) {
                    throw new Error(`applyPatch remove: id '${op.targetId}' bulunamadi. ` +
                        `Mevcut id'ler: [${collectIds(root).join(', ')}]`);
                }
                if (found.parent === null) {
                    throw new Error(`applyPatch remove: root node silinemez`);
                }
                found.parent.children = found.parent.children.filter((_, i) => i !== found.indexInParent);
                break;
            }
            case 'add': {
                if (!op.parentId) {
                    throw new Error(`applyPatch add: parentId bos veya tanimsiz — LLM patch gecersiz. ` +
                        `Mevcut id'ler: [${collectIds(root).join(', ')}]`);
                }
                const found = findById(root, op.parentId);
                if (!found) {
                    throw new Error(`applyPatch add: parentId '${op.parentId}' bulunamadi. ` +
                        `Mevcut id'ler: [${collectIds(root).join(', ')}]`);
                }
                if (!found.node.children) {
                    found.node.children = [];
                }
                const newNode = deepClone(op.node);
                const idx = op.index ?? found.node.children.length;
                found.node.children.splice(idx, 0, newNode);
                break;
            }
            case 'replace': {
                if (!op.targetId) {
                    throw new Error(`applyPatch replace: targetId bos veya tanimsiz — LLM patch gecersiz. ` +
                        `Mevcut id'ler: [${collectIds(root).join(', ')}]`);
                }
                const found = findById(root, op.targetId);
                if (!found) {
                    throw new Error(`applyPatch replace: id '${op.targetId}' bulunamadi. ` +
                        `Mevcut id'ler: [${collectIds(root).join(', ')}]`);
                }
                const newNode = deepClone(op.node);
                // targetId miras alinir
                newNode.id = op.targetId;
                if (found.parent === null) {
                    // Root replace
                    root = newNode;
                }
                else {
                    found.parent.children[found.indexInParent] = newNode;
                }
                break;
            }
        }
    }
    // Patch sonrasi schema validate — gecersiz/yarim patch'leri reject et
    const parsed = windowSpecSchema.safeParse(root);
    if (!parsed.success) {
        throw new Error(`applyPatch: patch sonrasi spec Zod schema dogrulamasinda basarisiz — ${parsed.error.message}`);
    }
    return root;
}
//# sourceMappingURL=mutateSpec.js.map